import { createStyles, rem } from "@mantine/core"

export const useStyles = createStyles(
    (
        theme,
        {
            aiSearchButtonIsVisible,
            favoriteButtonDisabled,
            favoriteButtonHidden,
            loggedInAsUser,
            popdownIsOpen,
            searchInputValue,
        }: {
            aiSearchButtonIsVisible: boolean
            favoriteButtonDisabled: boolean
            favoriteButtonHidden: boolean
            loggedInAsUser: boolean
            popdownIsOpen: boolean
            searchInputValue: string
        },
    ) => ({
        textInputContainer: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: "55rem",
        },
        // Currently shared by TextInput and Textarea versions of the search bar/input
        // which varies based on popdownIsOpen
        textarea: {
            width: "100%",
            borderRadius: "0.25rem",
            "&& .mantine-Textarea-input": {
                paddingLeft: aiSearchButtonIsVisible ? "3.5rem" : "2.5rem",
                paddingRight: searchInputValue && !favoriteButtonHidden ? "5rem" : "3.5rem",

                "::-webkit-scrollbar": {
                    width: "0.5rem",
                    background: "transparent",
                },

                "::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                    padding: "0.25rem",
                    backgroundClip: "padding-box",
                    border: "2px solid rgba(0, 0, 0, 0)",
                    borderRadius: "1rem",
                },

                "::-webkit-scrollbar-track": {
                    background: "transparent",
                    padding: "0.25rem",
                },
            },

            "&:focus": {
                boxShadow: "rgba(255, 255, 255, 0.48) 0px 0px 0px 2px",
            },
        },
        // Currently shared by TextInput and Textarea versions of the search bar/input
        // which varies based on popdownIsOpen
        input: {
            height: "2.5rem",
            backgroundColor: popdownIsOpen ? "white" : "rgba(255, 255, 255, 0.20)",
            border: popdownIsOpen ? `1px solid ${theme.colors.violet[7]}` : "1px solid transparent",
            color: "white",
            // This positioning is for the Textarea's input to expand downward—and
            // not upwards AND downwards—when long text is entered
            position: popdownIsOpen ? "absolute" : "relative",
            top: popdownIsOpen ? "-1.25rem" : null, // half of the min-height
            // These paddings are necessary w/the above positioning to size the
            // Mantine Textarea height and placeholder text like a TextInput
            // (min-height - line-height) / 2 - border-width
            // (2.5rem - 1.7rem) / 2 - 0.0625rem
            "--vertical-padding": "calc(0.4rem - 0.0625rem)",
            paddingTop: "var(--vertical-padding)",
            paddingBottom: "var(--vertical-padding)",
            fontSize: "0.875rem",
            lineHeight: "1.7rem",

            "&.mantine-Input-input": {
                textOverflow: popdownIsOpen ? null : "ellipsis",
                overflow: popdownIsOpen ? null : "hidden",
                paddingLeft: aiSearchButtonIsVisible ? "3.5rem" : "2.5rem",
                paddingRight: searchInputValue && !favoriteButtonHidden ? "5rem" : "3.5rem",
            },

            "::placeholder": {
                color: theme.colors.violet[1],
                opacity: 0.5,
            },

            ...(loggedInAsUser && {
                color: theme.colors.violet[8],

                "::placeholder": {
                    color: theme.colors.violet[8],
                    opacity: 0.5,
                },
            }),

            ...(popdownIsOpen && {
                color: theme.colors.gray[7],

                "::placeholder": {
                    color: theme.colors.gray[7],
                    opacity: 0.5,
                },
            }),
        },
        rightSectionButtons: {
            display: "flex",
            alignItems: "center",
            position: "absolute",
            right: "0.8rem",
        },
        favoriteButton: {
            color: "white",
            border: "none",
            width: "2rem",
            height: "2rem",
            background: "transparent",
            borderRadius: "1rem",
            transition: "background-color 0.25s ease-in-out, color 0.25s ease-in-out",
            cursor: favoriteButtonDisabled ? "default" : null,

            "&:hover": {
                backgroundColor: theme.colors.violet[2],
            },

            ...(loggedInAsUser && {
                color: theme.colors.violet[8],

                "&:hover": {
                    backgroundColor: theme.colors.violet[3],
                },
            }),

            ...(popdownIsOpen && {
                color: "black",

                "&:hover": {
                    backgroundColor: theme.colors.violet[1],
                },
            }),
        },
        clearButton: {
            color: "white",
            border: "none",
            width: "2rem",
            height: "2rem",
            background: "transparent",
            borderRadius: "1rem",
            transition: "background-color 0.25s ease-in-out, color 0.25s ease-in-out",

            "&:hover": {
                backgroundColor: theme.colors.violet[2],
            },

            ...(loggedInAsUser && {
                color: theme.colors.violet[8],

                "&:hover": {
                    backgroundColor: theme.colors.violet[3],
                },
            }),

            ...(popdownIsOpen && {
                color: "black",

                "&:hover": {
                    backgroundColor: theme.colors.violet[1],
                },
            }),
        },

        dropdown: {
            backgroundColor: "white",
            border: `${rem(1)} solid ${theme.colors.gray[2]}`,
            boxShadow: theme.shadows.xl,
        },
        item: {
            height: "2.5rem",
            width: "10rem",
            fontSize: "1rem",
        },
        itemLabel: {
            gap: "0.75rem",
        },
        itemIcon: {
            fontSize: "1rem",
        },
        leftSectionIcon: {
            pointerEvents: "auto",
        },
        searchDropdownButton: {
            display: "flex",
            alignItems: "center",
            marginLeft: "1.5rem",
            color: "white",

            ...(loggedInAsUser && {
                color: theme.colors.violet[8],
            }),

            ...(popdownIsOpen && {
                color: theme.colors.gray[7],
            }),
        },
        searchStandardOnlyButton: {
            display: "flex",
            alignItems: "center",
            marginLeft: "0.5rem",
            color: "white",

            ...(loggedInAsUser && {
                color: theme.colors.violet[8],
            }),

            ...(popdownIsOpen && {
                color: theme.colors.gray[7],
            }),
        },
        angleDownIcon: {
            fontSize: "0.875rem",
        },
    }),
)
